"use client"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  FlexColumn,
  Form,
  Input,
  Media,
  Popover,
  Separator,
  SpaceBetween,
  TextLabel,
} from "@opensea/ui-kit"
import { bn } from "@opensea/utils"
import { useState } from "react"
import { useForm, useFormContext, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { z } from "zod"
import { Button } from "../../../../../components/Button"
import { useIsFormSubmitDisabled } from "../../../../../lib/form/valid"
import { MINIMUM_LISTING_PRICE } from "../../../constants"
import type { ListingFlowFormValues } from "../../types"

const ladderSchema = z.object({
  startingPrice: z.float64().positive().optional(),
  increaseBy: z.float64().positive().optional(),
})

type LadderFormValues = z.infer<typeof ladderSchema>

export const LadderButton = () => {
  const t = useTranslations("wallet.LadderButton")
  const [isOpen, setIsOpen] = useState(false)
  const { control, setValue: parentSetValue } =
    useFormContext<ListingFlowFormValues>()

  const {
    register,
    handleSubmit,
    getValues,
    control: ladderControl,
  } = useForm<LadderFormValues>({
    defaultValues: {
      startingPrice: undefined,
      increaseBy: undefined,
    },
    mode: "onChange",
    resolver: zodResolver(ladderSchema),
  })
  const listings = useWatch({ control, name: "listings" })

  const isFormSubmitDisabled = useIsFormSubmitDisabled(ladderControl)

  if (listings.length === 1) {
    return null
  }

  const submitOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSubmit()
    }
  }

  const onSubmit = () => {
    if (isFormSubmitDisabled) {
      return
    }

    listings.forEach((_, index) => {
      parentSetValue(
        `listings.${index}.price`,
        bn(getValues("startingPrice") ?? 0)
          .plus(bn(index).multipliedBy(bn(getValues("increaseBy") ?? 0)))
          .toNumber(),
        { shouldValidate: true },
      )
    })
    setIsOpen(false)
  }

  const trigger = (
    <Button size="sm" variant="secondary">
      {t("ladder")}
    </Button>
  )

  const startingPriceInput = (
    <Input
      className="font-mono"
      endEnhancer={listings[0].listingSymbol}
      onKeyDown={submitOnEnter}
      placeholder="0.0"
      size="sm"
      step={MINIMUM_LISTING_PRICE}
      type="number"
      {...register("startingPrice", {
        setValueAs: value => (value ? Number.parseFloat(value) : undefined),
      })}
    />
  )

  const increaseByInput = (
    <Input
      className="font-mono"
      endEnhancer={listings[0].listingSymbol}
      onKeyDown={submitOnEnter}
      placeholder="0.0"
      size="sm"
      step={MINIMUM_LISTING_PRICE}
      type="number"
      {...register("increaseBy", {
        setValueAs: value => (value ? Number.parseFloat(value) : undefined),
      })}
    />
  )

  const submitButton = (
    <Button disabled={isFormSubmitDisabled} onClick={onSubmit} size="sm">
      {t("confirm")}
    </Button>
  )

  return (
    <>
      <Media lessThan="md">
        <BottomSheet
          content={
            <>
              <BottomSheetHeader>
                <BottomSheetHeaderTitle>{t("ladder")}</BottomSheetHeaderTitle>
              </BottomSheetHeader>
              <BottomSheetBody>
                <FlexColumn className="gap-3">
                  <SpaceBetween className="gap-3">
                    <FlexColumn className="grow gap-3">
                      <TextLabel color="text-secondary" size="2xs">
                        {t("startingPrice")}
                      </TextLabel>
                      {startingPriceInput}
                    </FlexColumn>
                    <Separator
                      className="h-auto flex-none"
                      orientation="vertical"
                    />
                    <FlexColumn className="grow gap-3">
                      <TextLabel color="text-secondary" size="2xs">
                        {t("increaseBy")}
                      </TextLabel>
                      {increaseByInput}
                    </FlexColumn>
                  </SpaceBetween>
                </FlexColumn>
              </BottomSheetBody>
              <BottomSheetFooter>{submitButton}</BottomSheetFooter>
            </>
          }
          nested
          onOpenChange={setIsOpen}
          open={isOpen}
        >
          {trigger}
        </BottomSheet>
      </Media>
      <Media greaterThanOrEqual="md">
        <Popover
          className="w-[144px] bg-bg-primary"
          content={
            <Form onSubmit={handleSubmit(onSubmit)}>
              <FlexColumn className="gap-3">
                <TextLabel color="text-secondary" size="2xs">
                  {t("startingPrice")}
                </TextLabel>
                {startingPriceInput}
                <Separator />
                <TextLabel color="text-secondary" size="2xs">
                  {t("increaseBy")}
                </TextLabel>
                {increaseByInput}
                {submitButton}
              </FlexColumn>
            </Form>
          }
          // Default styles dont work because portal is false. When portal is true, for some reason the tooltip closes on click as well.
          onOpenChange={setIsOpen}
          open={isOpen}
          portal={false}
        >
          {trigger}
        </Popover>
      </Media>
    </>
  )
}
